import type { DispatchValue } from '../pipeline/dispatch';
import type { ReviewSelection } from '../ui/types';
import type { TextDetection } from '../detect/pii/types';
import { redactText } from '../redact/text';
import { redactImage, type RedactRect } from '../redact/image';
import { isConstrainedDevice } from './device';
import { redactPdf } from '../redact/pdf';

export type RedactOutput = {
  blob: Blob;
  filename: string;
  /** MIME of the produced blob (useful for Content-Type when caching). */
  mime: string;
};

const textOutput = (
  value: Extract<DispatchValue, { kind: 'text' | 'csv' }>,
  selection: ReviewSelection,
): RedactOutput => {
  const cleaned = redactText(value.originalText, selection.textPii);
  const mime = value.kind === 'csv' ? 'text/csv' : 'text/plain';
  return {
    blob: new Blob([cleaned], { type: mime }),
    filename: value.outputFilename,
    mime,
  };
};

/** Pad a face bbox so the blur covers hair, ears, and neck — BlazeFace's
 *  output stops at the face proper, leaving identifying signal at the edges. */
const padFaceBbox = (
  bbox: { x: number; y: number; w: number; h: number },
  width: number,
  height: number,
  factor = 0.3,
): { x: number; y: number; w: number; h: number } => {
  const dx = bbox.w * factor;
  const dy = bbox.h * factor;
  const x = Math.max(0, bbox.x - dx);
  const y = Math.max(0, bbox.y - dy);
  const w = Math.min(width - x, bbox.w + 2 * dx);
  const h = Math.min(height - y, bbox.h + 2 * dy);
  return { x, y, w, h };
};

const imageOutput = async (
  value: Extract<DispatchValue, { kind: 'image' }>,
  selection: ReviewSelection,
): Promise<RedactOutput> => {
  const rects: RedactRect[] = [];
  const dims = { w: value.bitmap.width, h: value.bitmap.height };
  for (const face of selection.faces) {
    const padded = padFaceBbox(face.bbox, dims.w, dims.h);
    const blurPx = Math.max(48, Math.round(Math.max(padded.w, padded.h) * 0.18));
    rects.push({ ...padded, mode: 'blur', blurPx });
  }
  const manuals = selection.manualsByPage.get(0) ?? [];
  for (const m of manuals) {
    if (m.w <= 0 || m.h <= 0) continue;
    rects.push({
      x: Math.round(m.x * dims.w),
      y: Math.round(m.y * dims.h),
      w: Math.round(m.w * dims.w),
      h: Math.round(m.h * dims.h),
      mode: 'black',
    });
  }
  const exifChanged = selection.exifKeys.length !== value.exif.fields.length;
  if (rects.length === 0 && !exifChanged) {
    return {
      blob: value.imageBlob,
      filename: value.outputFilename,
      mime: value.mime,
    };
  }
  const format =
    value.mime === 'image/jpeg'
      ? 'image/jpeg'
      : value.mime === 'image/webp'
        ? 'image/webp'
        : 'image/png';
  const blob = await redactImage(value.bitmap, rects, { format });
  return { blob, filename: value.outputFilename, mime: format };
};

const pdfOutput = async (
  value: Extract<DispatchValue, { kind: 'pdf' }>,
  selection: ReviewSelection,
): Promise<RedactOutput> => {
  const selectionByPage = new Map<number, TextDetection[]>();
  for (const page of value.pages) selectionByPage.set(page.index, []);
  for (const det of selection.textPii) {
    for (const page of value.pages) {
      if (page.detections.includes(det)) {
        selectionByPage.get(page.index)?.push(det);
        break;
      }
    }
  }
  // Mobile WebKit kills tabs that spike past ~1 GB. At scale=2 a single
  // letter-size page canvas is ~7 MB and the embedded PNGs all stay
  // resident in pdf-lib until save(). Drop scale on small viewports
  // OR phones / low-memory devices (where the viewport check alone
  // misses iPad-in-desktop-mode running on an iPhone home-screen app).
  const blob = await redactPdf(value.pdfBytes, value.pages, selectionByPage, {
    scale: isConstrainedDevice() ? 1.4 : 2,
    manualsByPage: selection.manualsByPage,
  });
  return { blob, filename: value.outputFilename, mime: 'application/pdf' };
};

/**
 * Run the appropriate redactor for a dispatched file given a user selection.
 * Returns the output blob + the canonical output filename.
 */
export const redactDispatch = async (
  value: DispatchValue,
  selection: ReviewSelection,
): Promise<RedactOutput> => {
  if (value.kind === 'text' || value.kind === 'csv') return textOutput(value, selection);
  if (value.kind === 'image') return imageOutput(value, selection);
  return pdfOutput(value, selection);
};

/**
 * Default selection — every detected item turned on.
 */
export const defaultSelection = (value: DispatchValue): ReviewSelection => {
  const empty = new Map();
  if (value.kind === 'text' || value.kind === 'csv') {
    return {
      textPii: [...value.detections],
      exifKeys: [],
      faces: [],
      pdfMetaKeys: [],
      manualsByPage: empty,
    };
  }
  if (value.kind === 'image') {
    return {
      textPii: [],
      exifKeys: value.exif.fields.map((f) => f.key),
      faces: [...value.faces],
      pdfMetaKeys: [],
      manualsByPage: empty,
    };
  }
  const allText: TextDetection[] = [];
  for (const p of value.pages) allText.push(...p.detections);
  return {
    textPii: allText,
    exifKeys: [],
    faces: [],
    pdfMetaKeys: value.metadata.fields.map((f) => f.key),
    manualsByPage: empty,
  };
};
